import { asOneOf, useQuery } from "portal/utils/hooks/useQuery";
import {
  CategorizationMap,
  groupCategoryOptionsByRole,
  hasVisibleShootTime,
  PLANT_PROFILE_ROLES,
  resolveOriginCategory,
} from "portal/utils/categoryCollection/category";
import { CategorizedImages } from "../categorized/CategorizedImages";
import { classes } from "portal/theme/theme";
import { CopyCategoryToLibraryDialog } from "../copyToLibrary/CopyCategoryToLibraryDialog";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import {
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { isValidAlmanac } from "portal/utils/shootTime";
import { PlantProfileHistory } from "../history/PlantProfileHistory";
import { PlantProfileHistoryProvider } from "../history/VersionHistoryProvider";
import { PlantProfileInstructionsDialog } from "../instructions/PlantProfileInstructionsDialog";
import { PlantProfileRoleSectionHeader } from "../PlantProfileRoleSectionHeader";
import { PlantProfileTab, PlantProfileViewTab } from "./tabs";
import { RadioChipOption } from "../../RadioChips";
import { ShootTimeExpandButton } from "../ShootTimeExpandButton";
import { ShootTimeSection } from "portal/components/shootTime/ShootTimeSection";
import { TAB_QUERY } from "portal/utils/routing";
import { TabContext, TabPanel } from "@mui/lab";
import { ThumbnailControls } from "../ThumbnailControls";
import { UnsavedPlantProfile } from "portal/utils/categoryCollection/plantProfile";
import { useCanUpdateCategoryTemplate } from "portal/views/categoryTemplates/permissions";
import { useCarbonDefaultAlmanac } from "portal/components/shootTime/useCarbonDefaultAlmanac";
import { useCopyCategoryToLibrary } from "../copyToLibrary/useCopyCategoryToLibrary";
import { useProtectedThumbnailSize } from "../../images/imageSizing/useProtectedThumbnailSize";
import { useShootTimeExpanded } from "../useShootTimeExpanded";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import React, { FC, useMemo, useState } from "react";

export const PLANT_PROFILE_TAB_PANEL_CLASSES = "pt-2 pl-4 pr-0";

export interface PlantProfileViewProps {
  uuid: string;
  plantProfile: UnsavedPlantProfile;
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  currentVersionId?: string;
  customerUuid?: string;
  onRevert?: (revertToVersionId: string) => Promise<unknown>;
  isReverting: boolean;
  onRevive?: (
    predictionPointId?: string,
    categoryId?: string
  ) => void | Promise<unknown>;
}

export const PlantProfileView: FC<PlantProfileViewProps> = ({
  uuid,
  plantProfile,
  categoryOptions,
  categorizationMap,
  currentVersionId,
  customerUuid,
  onRevert,
  isReverting,
  onRevive,
}) => {
  const { t } = useTranslation();
  const { almanac: carbonDefaultAlmanac } = useCarbonDefaultAlmanac();
  const { categoryCollection } = useUserPreferences();
  const { isEnabled: hasCustomCategoriesFlag } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    customerUuid
  );
  const canUpdateCategoryTemplate = useCanUpdateCategoryTemplate();
  const canAddExistingToLibrary = Boolean(
    hasCustomCategoriesFlag && canUpdateCategoryTemplate
  );
  const { closeAlmanacDialog, createCategoryTemplate, pending, requestCopy } =
    useCopyCategoryToLibrary(customerUuid);
  const {
    thumbnailDivRef,
    measuredThumbnailMaxSize,
    thumbnailSize,
    dimensions,
  } = useProtectedThumbnailSize(categoryCollection.thumbnailSize);
  const [tabParam, setTab] = useQuery(
    TAB_QUERY,
    asOneOf(PlantProfileTab.CATEGORIZED, PlantProfileTab.HISTORY)
  );
  const tab = tabParam ?? PlantProfileTab.CATEGORIZED;
  const [showImages, setShowImages] = useState(true);
  const {
    isShootTimeExpanded,
    allShootTimeExpanded,
    setAllShootTimeExpanded,
    onShootTimeExpandedChange,
  } = useShootTimeExpanded(
    plantProfile.categories.map((category) => category.id)
  );

  const categoriesByRole = useMemo(
    () =>
      groupCategoryOptionsByRole(
        categoryOptions,
        plantProfile.categories,
        plantProfile.categoryRoles
      ),
    [categoryOptions, plantProfile.categories, plantProfile.categoryRoles]
  );
  const hasAnyAlmanac = useMemo(
    () =>
      plantProfile.categories.some((category) =>
        isValidAlmanac(category.almanac)
      ),
    [plantProfile.categories]
  );

  return (
    <TabContext value={tab}>
      <div className="flex flex-col md:flex-row justify-center md:justify-between items-start md:items-end">
        <div className="flex items-center gap-6">
          <Tabs
            value={tab}
            onChange={(
              _: React.SyntheticEvent,
              newValue: PlantProfileViewTab
            ) => {
              setTab(newValue);
            }}
          >
            <Tab
              label={t(
                "components.categoryCollectionProfile.images.categorized"
              )}
              className="text-inherit"
              value={PlantProfileTab.CATEGORIZED}
            />
            <Tab
              label={t("views.fleet.robots.history.navTitle")}
              className="text-inherit"
              value={PlantProfileTab.HISTORY}
            />
          </Tabs>
          <PlantProfileInstructionsDialog />
        </div>
        <ThumbnailControls
          measuredThumbnailMaxSize={measuredThumbnailMaxSize}
          thumbnailSize={thumbnailSize}
        />
      </div>
      {/* Note: overflow-visible on TabPanel is required for sticky controls */}
      <TabPanel
        value={PlantProfileTab.CATEGORIZED}
        className={classes(PLANT_PROFILE_TAB_PANEL_CLASSES, "overflow-visible")}
      >
        <div ref={thumbnailDivRef} className="flex flex-col gap-4">
          <Paper
            variant="section"
            elevation={0}
            className="sticky top-0 z-10 py-2 flex flex-wrap items-center justify-center gap-4 rounded-none"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showImages}
                  onChange={(_, checked) => setShowImages(checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption">
                  {t("components.categoryCollectionProfile.images.showImages")}
                </Typography>
              }
            />
            {hasAnyAlmanac && (
              <ShootTimeExpandButton
                expanded={allShootTimeExpanded}
                onToggle={() => setAllShootTimeExpanded(!allShootTimeExpanded)}
              />
            )}
          </Paper>
          {/** categorized images and shoot time */}
          {PLANT_PROFILE_ROLES.map((role) => {
            const options = categoriesByRole.get(role) ?? [];
            if (options.length === 0) {
              return;
            }
            return (
              <div key={role} className="flex flex-col gap-2">
                <PlantProfileRoleSectionHeader role={role} />
                {options.map((categoryOption) => {
                  const category = plantProfile.categories.find(
                    ({ id }) => id === categoryOption.id
                  );
                  const shootTime =
                    hasVisibleShootTime(role) &&
                    isValidAlmanac(category?.almanac) ? (
                      <ShootTimeSection
                        almanac={category.almanac}
                        carbonDefaultAlmanac={carbonDefaultAlmanac}
                        originCategory={resolveOriginCategory(
                          category.almanac.originCategoryId
                        )}
                        compact
                        expanded={isShootTimeExpanded(categoryOption.id)}
                        onExpandedChange={(expanded) => {
                          onShootTimeExpandedChange(
                            categoryOption.id,
                            expanded
                          );
                        }}
                      />
                    ) : undefined;
                  return (
                    <CategorizedImages
                      key={categoryOption.id}
                      categoryId={categoryOption.id}
                      categoryOptions={categoryOptions}
                      predictionPointIds={category?.chipIds ?? []}
                      dimensions={dimensions}
                      categorizationMap={categorizationMap}
                      showImages={showImages}
                      onRequestAddToLibrary={
                        canAddExistingToLibrary && category
                          ? () => {
                              requestCopy(category, role);
                            }
                          : undefined
                      }
                    >
                      {shootTime}
                    </CategorizedImages>
                  );
                })}
              </div>
            );
          })}
        </div>
      </TabPanel>
      {currentVersionId ? (
        <TabPanel
          value={PlantProfileTab.HISTORY}
          className={PLANT_PROFILE_TAB_PANEL_CLASSES}
        >
          <PlantProfileHistoryProvider
            plantProfileId={uuid}
            currentVersionId={currentVersionId}
          >
            <PlantProfileHistory
              uuid={uuid}
              dimensions={dimensions}
              onRevert={onRevert}
              isReverting={isReverting}
              onClassify={onRevive}
              currentVersionCategorizationMap={categorizationMap}
            />
          </PlantProfileHistoryProvider>
        </TabPanel>
      ) : undefined}
      {pending ? (
        <CopyCategoryToLibraryDialog
          category={pending.category}
          profileName={plantProfile.name}
          role={pending.role}
          createCategoryTemplate={createCategoryTemplate}
          customerUuid={customerUuid}
          onClose={closeAlmanacDialog}
        />
      ) : undefined}
    </TabContext>
  );
};
